import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Card, Row, Col, Statistic, Button, Space, Tag, Typography, Empty } from 'antd'
import {
  UserOutlined,
  HomeOutlined,
  SafetyOutlined,
  HistoryOutlined,
  UserAddOutlined,
  TeamOutlined,
  BankOutlined,
  CheckCircleOutlined,
  AuditOutlined,
} from '@ant-design/icons'
import { useAuth } from '@/hooks/useAuth'
import { usersApi } from '@/api/users'
import { hospitalsApi } from '@/api/hospitals'
import { permissionRequestsApi } from '@/api/permission-requests'

const { Title, Text } = Typography

export const Dashboard = () => {
  const { user } = useAuth()
  const navigate = useNavigate()

  // Fetch statistics
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users-stats'],
    queryFn: () => usersApi.getUsers({ page: 1, limit: 1 }),
  })

  const { data: hospitalsData, isLoading: hospitalsLoading } = useQuery({
    queryKey: ['hospitals-stats'],
    queryFn: () => hospitalsApi.getHospitals({ page: 1, pageSize: 1 }),
  })

  const { data: pendingApprovalsData, isLoading: approvalsLoading } = useQuery({
    queryKey: ['pending-approvals-stats'],
    queryFn: () => permissionRequestsApi.getPending(),
  })

  const totalUsers = usersData?.metadata?.pagination?.total ?? 0
  const totalHospitals = hospitalsData?.data?.total ?? 0
  const pendingApprovals = pendingApprovalsData?.data?.total ?? 0

  const quickActions = [
    {
      key: 'create-user',
      icon: <UserAddOutlined />,
      label: 'Create User',
      description: 'Add a new user to the system',
      onClick: () => navigate('/users/create'),
    },
    {
      key: 'view-users',
      icon: <TeamOutlined />,
      label: 'View All Users',
      description: 'Browse and manage users',
      onClick: () => navigate('/users'),
    },
    {
      key: 'view-hospitals',
      icon: <BankOutlined />,
      label: 'View All Hospitals',
      description: 'Browse hospital directory',
      onClick: () => navigate('/hospitals'),
    },
    {
      key: 'approvals',
      icon: <CheckCircleOutlined />,
      label: 'View Approvals',
      description: 'Review pending permission requests',
      onClick: () => navigate('/admin/approvals'),
      badge: pendingApprovals > 0 ? pendingApprovals : undefined,
    },
    {
      key: 'notifications',
      icon: <SafetyOutlined />,
      label: 'Notifications',
      description: 'View system notifications',
      onClick: () => navigate('/admin/notifications'),
    },
    {
      key: 'audit-log',
      icon: <AuditOutlined />,
      label: 'Audit Log',
      description: 'View system activity log',
      onClick: () => navigate('/admin/audit'),
    },
  ]

  return (
    <div>
      <Title level={2}>Welcome to X-Manager</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 32 }}>
        Hello, {user?.first_name} {user?.last_name}! Role: <Tag color="blue">{user?.role}</Tag>
      </Text>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={totalUsers}
              prefix={<UserOutlined />}
              loading={usersLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Hospitals"
              value={totalHospitals}
              prefix={<HomeOutlined />}
              loading={hospitalsLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Pending Approvals"
              value={pendingApprovals}
              prefix={<SafetyOutlined />}
              loading={approvalsLoading}
              valueStyle={{ color: pendingApprovals > 0 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Your Session"
              value={1}
              prefix={<HistoryOutlined />}
              suffix="active"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        {/* Quick Actions */}
        <Col xs={24} lg={12}>
          <Card title="Quick Actions">
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              {quickActions.map((action) => (
                <Button
                  key={action.key}
                  type="default"
                  size="large"
                  icon={action.icon}
                  onClick={action.onClick}
                  style={{
                    width: '100%',
                    height: 'auto',
                    padding: '12px 16px',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, marginBottom: 2 }}>
                      {action.label}
                      {action.badge !== undefined && (
                        <Tag
                          color="red"
                          style={{ marginLeft: 8, fontSize: '11px', padding: '0 4px' }}
                        >
                          {action.badge}
                        </Tag>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#888', fontWeight: 'normal' }}>
                      {action.description}
                    </div>
                  </div>
                </Button>
              ))}
            </Space>
          </Card>
        </Col>

        {/* Activity Visualization - Under Construction */}
        <Col xs={24} lg={12}>
          <Card title="Activity Metrics">
            <Empty
              description={
                <div>
                  <Text type="secondary">Activity visualization under construction</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Coming soon: Activity trends, peak usage times, and user engagement metrics
                  </Text>
                </div>
              }
            />
          </Card>
        </Col>
      </Row>
    </div>
  )
}
